import { Component, computed, inject, OnInit, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { idMapToArray, ScopeService } from '../../../../../@core/utils/scope.service';
import { NbFormFieldModule, NbInputModule, NbListModule } from '@nebular/theme';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InvestmentWizardService } from '../../../../../@core/utils/investment-wizard-service';
import { IInvestmentWizard } from '../wizard-interface';
import { PerimetersService, UsersService } from '../../../../../@core/utils';
import { KPI } from '../../../../../@core/api';

interface PerimeterWithPermissions extends KPI.Perimeter {
  can_write: boolean;
}

@Component({
  selector: 'app-wizard-site-selection',
  standalone: true,
  imports: [
    NbListModule,
    CommonModule,
    NbInputModule,
    NbFormFieldModule,
    TranslateModule,
  ],
  template: `
    <div class="m-2 flex-grow-1">
      <nb-form-field>
        <i class="fa fa-search" nbPrefix></i>
        <input nbInput fullWidth (input)="searchQuery.set($any($event.target).value)" [placeholder]="'Search' | translate">
      </nb-form-field>
      <nb-list class="flex-grow-1 wrapper">
        @for (site of availableSites(); track site) {
          <nb-list-item (click)="nextStep(site)" class="pointer" [class.text-secondary]="!site.can_write"
                        [class.selected]="state() && site.id === state().id">
            {{ site.name }}
          </nb-list-item>
        }
      </nb-list>
    </div>
  `,
  styleUrl: '../wizard-index.component.scss',
})
export class WizardSiteSelectionComponent implements OnInit, IInvestmentWizard {
  readonly stateKey = 'SITE_SELECTION';
  state = () => this.wizardService.getState(this.stateKey);

  // dependencies
  private scope = inject(ScopeService);
  private wizardService = inject(InvestmentWizardService);
  private pService = inject(PerimetersService);
  private translate = inject(TranslateService);
  private userService = inject(UsersService);

  private sites$ = this.scope.perimeters$.pipe(
    idMapToArray(),
  );

  private sites = toSignal(this.sites$, { initialValue: [] });
  private userPermissions = toSignal(this.userService.currentUserPerimeterPermissions$, { initialValue: [] });
  private selectedBuildings = toSignal(this.pService.buildingIds$, { initialValue: [] });

  searchQuery = signal<string>('');

  availableSites: Signal<PerimeterWithPermissions[]> = computed(() => {
    const selectedBuildings = this.selectedBuildings(),
      permissions = this.userPermissions();
    return this.sites().filter(site => {
      const matchTitle = site.name.toLowerCase().includes(this.searchQuery().toLowerCase()),
        buildings = site.sub_perimeters.map(p => p.building_id),
        hasBuilding = buildings.some(b => selectedBuildings.includes(b));
      return matchTitle && hasBuilding;
    }).map(site => {
      const sitePermissions = permissions.find(p => p.perimeter === site.id);
      site['can_write'] = !sitePermissions.read_only;
      return <PerimeterWithPermissions>site;
    });
  });

  ngOnInit(): void {
    const state = this.state(),
      availableSites = this.availableSites();
    if (!state && availableSites.length === 1) {
      const site = availableSites[0];
      if (site.can_write) this.nextStep(availableSites[0]);
      return;
    }
    if (!state) {
      this.wizardService.blockCurrentStep(this.translate.instant('Please select a site to continue'));
    } else {
      this.wizardService.unblockCurrentStep();
    }
  }

  nextStep(site: PerimeterWithPermissions): void {
    if (!site.can_write) return;
    this.wizardService.setState('SITE_SELECTION', site);
    this.wizardService.next();
  }

}
